import { ProjectBriefFields } from '__generated__/ProjectBriefFields';
import { StructuredBriefFile } from '__generated__/StructuredBriefFile';
import { ProjectTimeframe } from '__generated__/types';
import Bugsnag from 'common/utils/Bugsnag';
import { Currency, toUnit } from 'common/utils/currency';
import Attachments from 'projects/components/Attachments';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { Industries } from 'projects/util/industries';
import React from 'react';
import { FormattedNumber } from 'react-intl';

export interface Props {
    brief: {
        id: string;
        fields: ProjectBriefFields;
        designers: { displayName: string }[];
        category: {
            title: string;
            key: string;
        } | null;
    };
    industries: Industries;
}

class SummaryPage extends React.Component<Props> {
    componentDidMount() {
        trackViewedBriefEvent({
            id: this.props.brief.id,
            page: 'Summary',
        });
    }

    url(file: StructuredBriefFile) {
        return `https://${file.bucket}.s3-accelerate.amazonaws.com/${file.key}`;
    }

    agencyType() {
        const { brief } = this.props;
        const agencyType = brief.fields.agencyType;

        switch (agencyType) {
            case 'NO':
                return __("No, I'm not an agency");
            case 'YES_INTERESTED':
                return __("Yes and I'm interested in agency services");
            case 'YES_NOT_INTERESTED':
                return __("Yes and I'm NOT interested in agency services");
            default:
                return agencyType;
        }
    }

    profileApproval() {
        return this.props.brief.fields.profileApproval ? __('Yes') : __('No');
    }

    budget() {
        const { brief } = this.props;

        return brief.fields.budget === 0 ? (
            __('Any price')
        ) : (
            <FormattedNumber
                value={toUnit(brief.fields.budget, brief.fields.currency as Currency)}
                style="currency"
                currency={brief.fields.currency}
                minimumFractionDigits={0}
            />
        );
    }

    timeframe() {
        const { brief } = this.props;
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

        switch (brief.fields.timeframe) {
            case ProjectTimeframe.NOT_SURE:
                return __("Not sure. I'd like to chat with my designer");
            case ProjectTimeframe.FORTY_EIGHT_HOURS:
                return __('48 hours');
            case ProjectTimeframe.ONE_MONTH:
                return __('1 month');
            case ProjectTimeframe.ONE_WEEK:
                return __('1 week');
            case ProjectTimeframe.TWO_WEEKS:
                return __('2 weeks');
            case ProjectTimeframe.SPECIFIC_DATES:
                if (brief.fields.startDate && brief.fields.endDate) {
                    return (
                        new Intl.DateTimeFormat('en-US', options).format(
                            new Date(brief.fields.startDate)
                        ) +
                        ' - ' +
                        new Intl.DateTimeFormat('en-US', options).format(
                            new Date(brief.fields.endDate)
                        )
                    );
                } else {
                    Bugsnag.notify('start and end date not supplied for specific dates', {
                        metaData: { brief },
                    });
                }
            default:
                return __("Not sure. I'd like to chat with my designer");
        }
    }

    handleEditClick(page: 'AboutYou' | 'Details' | 'TimeAndBudget') {
        trackEditBriefEvent({
            id: this.props.brief.id,
            page: page,
        });
    }

    render() {
        return (
            <div className="content-setting">
                {this.renderDetailsSection()}
                {this.renderTimeAndBudgetSection()}
                {this.renderAboutYouSection()}
            </div>
        );
    }

    renderDetailsSection() {
        const { brief } = this.props;

        return (
            <SummaryCard
                heading={__('Tell us about your project')}
                onEditClick={() => this.handleEditClick('Details')}
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={__('Name your project')}>
                    {brief.fields.title}
                </SummaryCardItem>
                {brief.category && (
                    <SummaryCardItem title={__('Select category')}>
                        {brief.category.title}
                    </SummaryCardItem>
                )}
                <SummaryCardItem title={__('Describe your project')}>
                    {formatAndLinkify(brief.fields.description)}
                </SummaryCardItem>
                {brief.fields.usage != '' && (
                    <SummaryCardItem title={__('How will your design be used?')}>
                        {formatAndLinkify(brief.fields.usage)}
                    </SummaryCardItem>
                )}
                {brief.fields.referenceFiles.length > 0 && (
                    <SummaryCardItem title={__('Add reference files')}>
                        {Attachments(brief.fields.referenceFiles)}
                    </SummaryCardItem>
                )}
                {brief.fields.inspirationFiles.length > 0 && (
                    <SummaryCardItem title={__('Add inspiration files')}>
                        {Attachments(brief.fields.inspirationFiles)}
                    </SummaryCardItem>
                )}
                {brief.fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any inspiration on the Web')}>
                        {linkify(brief.fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        );
    }

    renderTimeAndBudgetSection() {
        const { brief } = this.props;
        return (
            <SummaryCard
                heading={__('Your time & budget')}
                onEditClick={() => this.handleEditClick('TimeAndBudget')}
                url={routes.brief(brief, 2)}
            >
                <SummaryCardItem title={__("What's your timeline?")}>
                    {this.timeframe()}
                </SummaryCardItem>
                <SummaryCardItem title={__("What's your budget?")}>{this.budget()}</SummaryCardItem>
            </SummaryCard>
        );
    }

    renderAboutYouSection() {
        const { brief } = this.props;
        const industry = this.props.industries.find(brief.fields.industry);
        return (
            <SummaryCard
                heading={__('About you')}
                onEditClick={() => this.handleEditClick('AboutYou')}
                url={routes.brief(brief, 3)}
            >
                {industry && (
                    <SummaryCardItem title={__('What industry are you in?')}>
                        {industry}
                    </SummaryCardItem>
                )}
                {brief.fields.urls.length > 0 && (
                    <SummaryCardItem title={__('Add your website and/or social media pages')}>
                        {linkify(brief.fields.urls)}
                    </SummaryCardItem>
                )}
                {(brief.fields.agencyType as string) != '' && (
                    <SummaryCardItem
                        title={__('Is your company a digital, marketing or design agency?')}
                    >
                        {this.agencyType()}
                    </SummaryCardItem>
                )}
                <SummaryCardItem
                    title={__(
                        'Can your designer showcase the finished designs in their 99designs portfolio?'
                    )}
                >
                    {this.profileApproval()}
                </SummaryCardItem>
                {brief.fields.other != '' && (
                    <SummaryCardItem
                        title={__("Anything else you'd like to share with your designer?")}
                    >
                        {formatAndLinkify(brief.fields.other)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        );
    }
}

export default SummaryPage;
